import React, { useState, useCallback, useMemo } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react'; // Ensure @headlessui/react is installed

// ====================================================================
// Mock Data Store
// ====================================================================

const initialPolicies = [
    {
        id: 'P001',
        name: 'Standard Office Policy',
        policyType: 'REGULAR',
        status: 'Active',
        rules: [
            { id: 1, type: 'shift_start', value: '9:00 AM' },
            { id: 2, type: 'late_grace', value: '10 min' },
            { id: 3, type: 'overtime_start', value: '9 hours' },
        ],
    },
    {
        id: 'P002',
        name: 'Remote Work Policy',
        policyType: 'FLEXI',
        status: 'Draft',
        rules: [
            { id: 4, type: 'daily_hours', value: '8 hours' },
            { id: 5, type: 'core_hours', value: '10 AM - 4 PM' },
        ],
    },
];

const initialRule = { id: Date.now(), type: 'late_grace', value: '10 min' };
const ruleOptions = [
    { value: 'shift_start', label: 'Shift Start Time' },
    { value: 'shift_end', label: 'Shift End Time' },
    { value: 'late_grace', label: 'Late Grace Period' },
    { value: 'overtime_start', label: 'Overtime Threshold' },
    { value: 'daily_hours', label: 'Mandatory Daily Hours' },
    { value: 'core_hours', label: 'Core Attendance Hours' },
];

// ====================================================================
// 1. PolicyFormModal Component (Nested)
//    - Clean, responsive Keka/Zoho-style modal
// ====================================================================

const PolicyFormModal = ({ isOpen, onClose, onSave, policy }) => {
    const isNew = useMemo(() => policy === null, [policy]);

    const [policyData, setPolicyData] = useState(() => ({
        id: policy?.id || null,
        name: policy?.name || '',
        policyType: policy?.policyType || 'REGULAR',
        status: policy?.status || 'Draft',
        rules: policy?.rules.length > 0 ? policy.rules : [initialRule],
    }));

    // --- Rule Management ---
    const handleAddRule = useCallback(() => {
        setPolicyData(prev => ({
            ...prev,
            rules: [...prev.rules, { id: Date.now(), type: 'shift_start', value: '9:00 AM' }],
        }));
    }, []);

    const handleRemoveRule = useCallback((id) => {
        setPolicyData(prev => ({
            ...prev,
            rules: prev.rules.filter(rule => rule.id !== id),
        }));
    }, []);

    const handleRuleChange = useCallback((id, field, value) => {
        setPolicyData(prev => ({
            ...prev,
            rules: prev.rules.map(rule => 
                rule.id === id ? { ...rule, [field]: value } : rule
            ),
        }));
    }, []);

    // --- General Form Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPolicyData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const cleanedRules = policyData.rules.filter(r => r.value.trim() !== '');
        
        if (!policyData.name.trim() || cleanedRules.length === 0) {
            alert('Please provide a policy name and at least one rule.');
            return;
        }

        onSave({ 
            policy: { ...policyData, rules: cleanedRules },
            isNew 
        });
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                {/* Backdrop */}
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel 
                                className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                // Small font size for Keka/Zoho feel
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                {/* Modal Header */}
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-lg font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Create New Attendance Policy' : `Edit Policy: ${policyData.name}`}
                                    <button 
                                        onClick={onClose}
                                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-5 text-sm">
                                    {/* Policy Details */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="name" className="block text-xs font-medium text-gray-700">Policy Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={policyData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="policyType" className="block text-xs font-medium text-gray-700">Policy Type</label>
                                            <select
                                                name="policyType"
                                                id="policyType"
                                                value={policyData.policyType}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                <option value="REGULAR">Regular (Fixed Shift)</option>
                                                <option value="FLEXI">Flexi-Time</option>
                                                <option value="HYBRID">Hybrid</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    {/* Policy Rules Setup */}
                                    <div className="space-y-3 pt-3 border-t border-gray-100">
                                        <h4 className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                            Attendance Rules
                                        </h4>

                                        {policyData.rules.map((rule) => (
                                            <div key={rule.id} className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                {/* Rule Type Select */}
                                                <div className="w-full sm:w-2/5">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1 sm:hidden">Rule</label>
                                                    <select
                                                        value={rule.type}
                                                        onChange={(e) => handleRuleChange(rule.id, 'type', e.target.value)}
                                                        className="block w-full rounded-md border-gray-300 shadow-sm text-xs h-8 p-1.5"
                                                    >
                                                        {ruleOptions.map(option => (
                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Rule Value Input */}
                                                <div className="w-full sm:w-2/5">
                                                    <label className="block text-xs font-medium text-gray-500 mb-1 sm:hidden">Value</label>
                                                    <input
                                                        type="text"
                                                        value={rule.value}
                                                        onChange={(e) => handleRuleChange(rule.id, 'value', e.target.value)}
                                                        placeholder="e.g., 9:00 AM, 30 min, 8 hours"
                                                        required
                                                        className="block w-full rounded-md border-gray-300 shadow-sm text-xs h-8 p-1.5"
                                                    />
                                                </div>

                                                {/* Action Button */}
                                                <div className="w-full sm:w-1/5 flex justify-end">
                                                    {policyData.rules.length > 1 ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveRule(rule.id)}
                                                            className="text-red-500 hover:text-red-700 p-1 transition"
                                                            title="Remove Rule"
                                                        >
                                                            <XMarkIcon className="w-4 h-4" />
                                                        </button>
                                                    ) : (
                                                        <div className="w-4 h-4"></div> // Placeholder for alignment
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            onClick={handleAddRule}
                                            className="text-sm text-indigo-600 hover:text-indigo-800 font-semibold mt-2"
                                        >
                                            + Add New Rule
                                        </button>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        >
                                            {isNew ? 'Create Policy' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// ====================================================================
// 2. PolicyList Component (Nested)
//    - Responsive table structure
// ====================================================================

const PolicyList = ({ policies, onEdit, onDelete }) => {
    const getPolicyTag = (status) => {
        let colorClass = '';
        switch (status) {
            case 'Active':
                colorClass = 'bg-green-100 text-green-800';
                break;
            case 'Draft':
                colorClass = 'bg-yellow-100 text-yellow-800';
                break;
            default:
                colorClass = 'bg-gray-100 text-gray-600';
        }
        return (
            <span 
                className={`px-3 py-1 text-xs font-semibold rounded-full ${colorClass}`}
                style={{ fontFamily: 'Roboto, sans-serif' }}
            >
                {status}
            </span>
        );
    };

    return (
        <div className="mt-8 bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="p-4 sm:p-6">
                <h3 className="text-xl font-semibold text-gray-700 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Current Policies
                </h3>
            </div>
            
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {['Policy ID', 'Policy Name', 'Type', 'Status', 'Rules Count', 'Actions'].map(header => (
                                <th
                                    key={header}
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    style={{ fontFamily: 'Roboto, sans-serif' }}
                                >
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {policies.map((policy) => (
                            <tr 
                                key={policy.id} 
                                className="hover:bg-gray-50 transition duration-100"
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ fontFamily: 'Roboto, sans-serif' }}>
                                    {policy.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                    {policy.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" style={{ fontFamily: 'Roboto, sans-serif' }}>
                                    {policy.policyType}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getPolicyTag(policy.status)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" style={{ fontFamily: 'Roboto, sans-serif' }}>
                                    {policy.rules.length}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                    <button 
                                        onClick={() => onEdit(policy)}
                                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50 transition"
                                        title="Edit Policy"
                                    >
                                        <PencilIcon className="w-4 h-4" />
                                    </button>
                                    <button 
                                        onClick={() => onDelete(policy.id)}
                                        className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50 transition"
                                        title="Delete Policy"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {policies.length === 0 && (
                            <tr>
                                <td colSpan="6" className="px-6 py-10 text-center text-gray-500 text-sm italic">
                                    No attendance policies found. Click "Create New Policy" to begin.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ====================================================================
// 3. AttendancePolicySetup (Main Component)
// ====================================================================

const AttendancePolicySetup = () => {
    // State for all policies
    const [policies, setPolicies] = useState(initialPolicies);
    
    // State for modal visibility and the policy currently being edited
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPolicy, setCurrentPolicy] = useState(null);

    // --- Handlers ---
    
    const handleCreatePolicy = useCallback(() => {
        setCurrentPolicy(null);
        setIsModalOpen(true);
    }, []);

    const handleEditPolicy = useCallback((policy) => {
        setCurrentPolicy(policy);
        setIsModalOpen(true);
    }, []);

    const handleDeletePolicy = useCallback((id) => {
        if (window.confirm('Are you sure you want to delete this policy?')) {
            setPolicies(prev => prev.filter(p => p.id !== id));
        }
    }, []);

    const handleSavePolicy = useCallback((newPolicyData) => {
        if (newPolicyData.isNew) {
            // Add new policy
            setPolicies(prev => [...prev, { 
                ...newPolicyData.policy, 
                id: `P${String(prev.length + 1).padStart(3, '0')}`,
                status: 'Draft'
            }]);
        } else {
            // Update existing policy
            setPolicies(prev => prev.map(p => 
                p.id === newPolicyData.policy.id ? newPolicyData.policy : p
            ));
        }
        setIsModalOpen(false);
    }, []);

    const policyCount = useMemo(() => policies.length, [policies]);

    return (
        <div 
            className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50"
            // Set default font for the entire page container
            style={{ fontFamily: 'Roboto, sans-serif' }}
        >
            <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-6 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Attendance Policy Setup
                </h1>
                <div className="text-sm text-gray-500 mt-2 sm:mt-0">
                    Total Policies: {policyCount}
                </div>
            </header>

            <div className="mt-6">
                <button
                    onClick={handleCreatePolicy}
                    className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                    <PlusIcon className="w-5 h-5" />
                    <span style={{ fontFamily: 'Roboto, sans-serif' }}>Create New Policy</span>
                </button>
            </div>

            <PolicyList 
                policies={policies}
                onEdit={handleEditPolicy}
                onDelete={handleDeletePolicy}
            />

            {/* Policy Form Modal */}
            {isModalOpen && (
                <PolicyFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSavePolicy}
                    policy={currentPolicy}
                />
            )}
        </div>
    );
};

export default AttendancePolicySetup;